/**
 * Participant Post-Session API Service (MED-806 / E3.4).
 *
 * Participant-facing endpoints backing the post-session page. All routes
 * are gated by the `live_session_delivery` feature flag at the API.
 *
 * Notes and commitment WRITES reuse the existing active-session endpoints
 * (`PUT /:sessionId/notes`, `PUT /:sessionId/commitment`) from MED-805 — only
 * the post-session-specific surfaces (feedback, summary, post-session step
 * state) live here.
 */

import apiClient from './client';
// MED-1088 — reuse the peer-leader callout wire type so both post-session
// bundles share one shape (mirrors the backend, where it lives in
// types/session.ts and is resolved by SessionRepository.findNextSessionLeaderCallout).
import type { NextSessionCallout } from './peerLeaderPostSession';

export type { NextSessionCallout };

// ============================================================================
// Types — mirror medley-api/types/participantPostSession.ts
// ============================================================================

export type FeedbackStatus = 'draft' | 'submitted';
export type FeedbackSidebarStatus = 'not_started' | 'in_progress' | 'submitted';

/**
 * Five 1–5 "agreement" scale answers (Strongly disagree … Strongly agree),
 * MED-989 / E3.4a AC1. Higher = stronger agreement. Nullable while draft;
 * all required at submission.
 */
export interface ParticipantFeedbackAgreementScales {
  present_intentional: number | null;
  group_understood: number | null;
  felt_open: number | null;
  thoughts_expanded: number | null;
  action_step: number | null;
}

export interface ParticipantFeedbackPayload extends ParticipantFeedbackAgreementScales {
  satisfaction_rating: number | null;
  // Open-text (AC2 — all optional). what_for_next (AC4) is SGOS-specific and
  // leader-visible; the others are Medley measurement data (not leader-visible, AC7).
  what_enjoyed: string;
  what_improvements: string;
  anything_else: string;
  what_for_next: string;
  status: FeedbackStatus;
  submitted_at: string | null;
  updated_at: string | null;
}

export interface ParticipantPostSessionStatePayload {
  notes_reviewed_at: string | null;
  completed_at: string | null;
}

export interface PostSessionSidebarSessionSummary {
  session_id: string;
  duration_minutes: number | null;
  scheduled_duration_minutes: number | null;
  session_number: number | null;
  total_sessions: number | null;
  leader_first_name: string;
  leader_display_name: string;
  feedback_status: FeedbackSidebarStatus;
}

export interface PostSessionSidebarNextSession {
  session_id: string;
  /** Raw `sessions.title` — workshop/standalone fallback for the label. */
  title: string;
  /** MED-1146 (AC-C3) — display-aware resolved topic (nullable); FE composes label. */
  session_topic: string | null;
  session_number: number | null;
  scheduled_start: string | null;
  group_name: string | null;
  participant_role_label: 'PARTICIPANT';
}

export interface PostSessionSidebarLeaderVisibility {
  leader_first_name: string;
  commitment_tracking_enabled: boolean;
}

export interface ParticipantPostSessionSummary {
  session_id: string;
  cohort_id: string;
  // MED-1180 — per-cohort LSD gate. Client defense-in-depth: render
  // FeaturesDisabledPage when false (backend also hard-404s the route).
  live_session_delivery_enabled: boolean;
  /**
   * MED-1199 (Bug 2 / AC7) — true when the cohort's group_coaching modality is
   * peer_led. Drives hiding the coach-oriented "What your coach sees" card.
   */
  is_peer_led: boolean;
  cohort_name: string;
  program_name: string;
  organization_name: string;
  /** Raw `sessions.title` — workshop/standalone fallback for the label. */
  session_title: string;
  /** MED-1146 (AC-C1) — display-aware resolved topic (nullable); FE composes label. */
  session_topic: string | null;
  /** MED-1146 (AC-C1) — group name for the no-topic fallback (Session N: {group}). */
  group_name: string | null;
  session_status: string;
  commitment_tracking_enabled: boolean;
  is_done: boolean;
  /**
   * MED-1063 (AC3) — false when the participant was marked `absent`; the page
   * hides the editable feedback form. Already-submitted feedback is still
   * returned and rendered read-only (AC5). Availability is otherwise immediate
   * from session end (AC1).
   */
  feedback_available: boolean;
  feedback: ParticipantFeedbackPayload;
  notes_html: string;
  commitment_text: string;
  /**
   * MED-1216 / AC8 — true when the coach entered this commitment and the
   * participant has not yet saved their own. The FinalizeCommitmentCard then
   * defaults to an editable review state ("noted by your coach, please review
   * and update if needed") instead of a plain read-only display.
   */
  commitment_authored_by_coach: boolean;
  post_session_state: ParticipantPostSessionStatePayload;
  sidebar_session_summary: PostSessionSidebarSessionSummary;
  sidebar_next_session: PostSessionSidebarNextSession | null;
  sidebar_leader_visibility: PostSessionSidebarLeaderVisibility;
  /**
   * MED-1088 — set only when the viewing participant is the designated leader
   * of the next upcoming session. Drives the "You're leading Session N → Go to
   * Session Prep" callout (the same component the peer-leader page uses). Null
   * for the normal, non-leading case — the sidebar Next Session card (MED-1059)
   * is unchanged.
   */
  next_session_callout: NextSessionCallout | null;
  /** MED-1193 (B9) — session themes, PLAIN register only. Null when AI wasn't active. */
  themes: {
    status: 'available' | 'pending' | 'not_available';
    themes?: Array<{ title: string; description: string }>;
  } | null;
}

export interface UpsertFeedbackDraftRequest {
  satisfaction_rating?: number | null;
  present_intentional?: number | null;
  group_understood?: number | null;
  felt_open?: number | null;
  thoughts_expanded?: number | null;
  action_step?: number | null;
  what_enjoyed?: string;
  what_improvements?: string;
  anything_else?: string;
  what_for_next?: string;
}

export interface SubmitFeedbackRequest {
  satisfaction_rating: number;
  present_intentional: number;
  group_understood: number;
  felt_open: number;
  thoughts_expanded: number;
  action_step: number;
  what_enjoyed?: string;
  what_improvements?: string;
  anything_else?: string;
  what_for_next?: string;
}

// ============================================================================
// API methods
// ============================================================================

const base = (sessionId: string) => `/api/participant/sessions/${sessionId}/post-session`;

export const participantPostSessionApi = {
  getSummary: (sessionId: string): Promise<ParticipantPostSessionSummary> =>
    apiClient.get<ParticipantPostSessionSummary>(`${base(sessionId)}/summary`),

  getFeedback: (sessionId: string): Promise<ParticipantFeedbackPayload> =>
    apiClient.get<ParticipantFeedbackPayload>(`${base(sessionId)}/feedback`),

  saveFeedbackDraft: (
    sessionId: string,
    body: UpsertFeedbackDraftRequest,
  ): Promise<ParticipantFeedbackPayload> =>
    apiClient.put<ParticipantFeedbackPayload>(`${base(sessionId)}/feedback`, body),

  submitFeedback: (
    sessionId: string,
    body: SubmitFeedbackRequest,
  ): Promise<ParticipantFeedbackPayload> =>
    apiClient.post<ParticipantFeedbackPayload>(`${base(sessionId)}/feedback/submit`, body),

  markNotesReviewed: (sessionId: string): Promise<ParticipantPostSessionStatePayload> =>
    apiClient.put<ParticipantPostSessionStatePayload>(`${base(sessionId)}/notes-reviewed`, {}),

  // Notes + commitment writes reuse the active-session endpoints (MED-805).
  upsertNotes: (sessionId: string, notesHtml: string): Promise<{ notes_html: string }> =>
    apiClient.put<{ notes_html: string }>(`/api/participant/sessions/${sessionId}/notes`, {
      notes_html: notesHtml,
    }),

  upsertCommitment: (
    sessionId: string,
    commitmentText: string,
  ): Promise<{ commitment_text: string }> =>
    apiClient.put<{ commitment_text: string }>(
      `/api/participant/sessions/${sessionId}/commitment`,
      { commitment_text: commitmentText },
    ),
};
